import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dagre from "@dagrejs/dagre";
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  ConnectionMode,
  useReactFlow,
  ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCollide,
  forceX,
  forceY,
  SimulationNodeDatum,
  SimulationLinkDatum,
} from "d3-force";
import { useGraphStore } from "../../store/graphStore";
import { CardNode } from "./CardNode";
import { CircularNode } from "./CircularNode";
import { CurvedEdge } from "./CurvedEdge";
import { GraphNodeData, GraphFlowNode, ConnectionChild } from "./types";
import { NodeContextMenu } from "../NodeContextMenu";
import { ZoomControls } from "../ZoomControls";
import { GraphNode, GraphEdge, NodeType } from "../../types/graph";

// D3 types
interface D3Node extends SimulationNodeDatum {
  id: string;
  isCard: boolean;
  nodeType?: string;
  fx?: number | null;
  fy?: number | null;
}

interface D3Link extends SimulationLinkDatum<D3Node> {
  source: string | D3Node;
  target: string | D3Node;
}

const nodeTypes = {
  cardNode: CardNode,
  circularNode: CircularNode,
};

const edgeTypes = {
  curved: CurvedEdge,
};

// Hierarchy rules for infinite expansion
// These define what child types each node type can connect to:
// Users → Roles → Resources → Users/SAs (circular)
// Service Accounts → Roles → Resources → Users/SAs (circular)
// Resources → Users/SAs → Roles (circular)
// Alerts → Resources → Users/SAs → Roles (circular)
// NOTE: Alerts can connect to both resources (in user/SA graphs) AND users (in resource graphs)
const INFINITE_HIERARCHY_MAP: Record<NodeType, NodeType[]> = {
  user: ["role", "anomaly", "secret"], // Users connect to roles, alerts, and secrets (correlations)
  service_account: ["role", "anomaly", "secret"], // SAs connect to roles, alerts, and secrets
  resource: ["user", "service_account", "anomaly"], // Resources connect to users, SAs, alerts
  role: ["resource"], // Roles connect to resources
  anomaly: ["resource", "user", "service_account"], // Alerts connect to resources OR users
  permission: [],
  key: [],
  token: ["resource"], // SaaS apps connect to SaaS resources (correlations)
  secret: ["token"], // Cloud secrets connect to SaaS apps (correlations)
  group: [],
};

const SHOW_FULL_GRAPH_DEPTH: Record<NodeType, number> = {
  user: 2,
  service_account: 2,
  resource: 2,
  role: 1,
  anomaly: 0,
  permission: 0,
  key: 0,
  token: 0,
  secret: 0,
  group: 0,
};

function ReactFlowGraphInner() {
  const {
    nodes: storeNodes,
    edges: storeEdges,
    selectNode,
    theme,
    activeConnectionType,
    expandedNodes,
    collapsedChildren,
    expandCollapsedGroup,
    focusMode,
    toggleFocusMode,
    focusedGraphId,
    setFocusedGraph,
    activeExpansionPanel,
    openExpansionPanel,
    closeExpansionPanel,
    promoteChild,
    promotedChildren,
    collapseStep,
    highlightedNodes,
    triggerZoomToNode,
    setTriggerZoomToNode,
    triggerBeautify,
    isBeautified,
    setBeautified,
    activeMode,
  } = useGraphStore();

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<GraphNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const { fitView, getZoom, setViewport, getViewport, getNodes } = useReactFlow();
  const [zoom, setZoom] = useState(1);
  const [viewportTrigger, setViewportTrigger] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<ReturnType<typeof forceSimulation<D3Node>> | null>(null);
  const layoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastVisibleIdsRef = useRef<string>("");
  const hasInitialFitRef = useRef(false);
  const nodePositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    node: { id: string; label: string; type: string } | null;
  }>({ isOpen: false, node: null });

  // Smooth zoom to highlighted node when triggered
  useEffect(() => {
    if (!triggerZoomToNode) return;

    const targetNode = nodes.find((n) => n.id === triggerZoomToNode);
    if (!targetNode) {
      setTriggerZoomToNode(null);
      return;
    }

    // Calculate center of node
    const nodeWidth = targetNode.type === "cardNode" ? 640 : 90;
    const nodeHeight = targetNode.type === "cardNode" ? 220 : 90;
    const targetX = targetNode.position.x + nodeWidth / 2;
    const targetY = targetNode.position.y + nodeHeight / 2;

    const containerWidth = containerRef.current?.offsetWidth || 800;
    const containerHeight = containerRef.current?.offsetHeight || 600;

    // Smooth zoom animation
    const targetZoom = 0.4;
    const currentViewport = getViewport();
    const startX = currentViewport.x;
    const startY = currentViewport.y;
    const startZoom = currentViewport.zoom;

    const endX = containerWidth / 2 - targetX * targetZoom;
    const endY = containerHeight / 2 - targetY * targetZoom;

    const duration = 600;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);

      const x = startX + (endX - startX) * eased;
      const y = startY + (endY - startY) * eased;
      const z = startZoom + (targetZoom - startZoom) * eased;

      setViewport({ x, y, zoom: z });
      setZoom(z);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setTriggerZoomToNode(null);
      }
    };

    requestAnimationFrame(animate);
  }, [triggerZoomToNode, nodes, getViewport, setViewport, setTriggerZoomToNode]);

  // Get children of a node
  const getNodeChildren = useCallback(
    (nodeId: string, nodeType: NodeType): string[] => {
      const allowedChildTypes = INFINITE_HIERARCHY_MAP[nodeType] || [];
      const children: string[] = [];

      storeEdges.forEach((edge) => {
        if (edge.source === nodeId) {
          const targetNode = storeNodes.find((n) => n.id === edge.target);
          if (targetNode && targetNode.type !== "permission" && allowedChildTypes.includes(targetNode.type)) {
            children.push(edge.target);
          }
        } else if (edge.target === nodeId) {
          const sourceNode = storeNodes.find((n) => n.id === edge.source);
          if (sourceNode && sourceNode.type !== "permission" && allowedChildTypes.includes(sourceNode.type)) {
            children.push(edge.source);
          }
        }
      });

      return [...new Set(children)];
    },
    [storeNodes, storeEdges],
  );

  // Check if node is root entity
  const isRootEntityNode = useCallback((node: GraphNode): boolean => {
    if (node.props?.isAlertHierarchy || node.props?.isExtendedHierarchy) return false;
    if (node.type === "user" && node.id.startsWith("user-")) return true;
    if (node.type === "service_account" && node.id.startsWith("sa-")) return true;
    if (node.type === "resource" && node.id.startsWith("resource-center-")) return true;
    return false;
  }, []);

  // Build visible node IDs
  const visibleNodeIds = useMemo(() => {
    const visible = new Set<string>();
    const nodeDetailsMap = new Map(storeNodes.map((n) => [n.id, n]));

    // In correlations/blast-radius mode, show only root entity nodes initially
    if (activeMode === "correlations" || activeMode === "blast-radius") {
      const entityPrefix = activeMode === "correlations" ? "corr-entity-" : "blast-entity-";
      storeNodes.forEach((node) => {
        if (node.id.startsWith(entityPrefix)) {
          visible.add(node.id);
        }
      });

      // Process expanded nodes for correlations/blast-radius
      const processExpandedMode = (nodeId: string) => {
        const node = nodeDetailsMap.get(nodeId);
        if (!node) return;
        const children = getNodeChildren(nodeId, node.type);
        const promoted = promotedChildren.get(nodeId);
        if (promoted) {
          children.forEach((childId) => {
            if (promoted.has(childId) && !visible.has(childId)) {
              visible.add(childId);
              if (expandedNodes.has(childId)) {
                processExpandedMode(childId);
              }
            }
          });
        }
      };
      Array.from(expandedNodes).forEach((nodeId) => {
        if (visible.has(nodeId)) processExpandedMode(nodeId);
      });

      return visible;
    }

    // Focus mode logic
    if (!focusMode && focusedGraphId) {
      storeNodes.forEach((node) => {
        if (node.type === "permission") return;
        if (node.props?.graphId === focusedGraphId && isRootEntityNode(node)) {
          visible.add(node.id);
        }
      });

      // Add anomalies
      storeEdges.forEach((edge) => {
        if (edge.type === "emits_anomaly") {
          const targetNode = nodeDetailsMap.get(edge.target);
          if (visible.has(edge.source) && targetNode?.type === "anomaly" && !targetNode.props?.isAlertHierarchy) {
            visible.add(edge.target);
          }
          const sourceNode = nodeDetailsMap.get(edge.source);
          if (visible.has(edge.target) && sourceNode?.type === "anomaly" && !sourceNode.props?.isAlertHierarchy) {
            visible.add(edge.source);
          }
        }
      });
    } else {
      // Multi-select mode
      storeNodes.forEach((node) => {
        if (node.type === "permission") return;
        if (node.props?.isAlertHierarchy || node.props?.isExtendedHierarchy) return;

        if (activeConnectionType === "users" && node.type === "user" && node.id.startsWith("user-")) {
          visible.add(node.id);
        } else if (activeConnectionType === "sa" && node.type === "service_account" && node.id.startsWith("sa-")) {
          visible.add(node.id);
        } else if (
          activeConnectionType === "resources" &&
          node.type === "resource" &&
          node.id.startsWith("resource-center-")
        ) {
          visible.add(node.id);
        }
      });

      // Add anomalies
      storeEdges.forEach((edge) => {
        if (edge.type === "emits_anomaly") {
          const sourceNode = nodeDetailsMap.get(edge.source);
          const targetNode = nodeDetailsMap.get(edge.target);
          if (visible.has(edge.source) && targetNode?.type === "anomaly" && !targetNode.props?.isAlertHierarchy) {
            visible.add(edge.target);
          }
          if (visible.has(edge.target) && sourceNode?.type === "anomaly" && !sourceNode.props?.isAlertHierarchy) {
            visible.add(edge.source);
          }
        }
      });
    }

    // Process expanded nodes
    const processExpanded = (nodeId: string) => {
      const node = nodeDetailsMap.get(nodeId);
      if (!node) return;

      const children = getNodeChildren(nodeId, node.type);
      const promoted = promotedChildren.get(nodeId);

      if (promoted) {
        children.forEach((childId) => {
          if (promoted.has(childId) && !visible.has(childId)) {
            visible.add(childId);
            if (expandedNodes.has(childId)) {
              processExpanded(childId);
            }
          }
        });
      }
    };

    Array.from(expandedNodes).forEach((nodeId) => {
      if (visible.has(nodeId)) {
        processExpanded(nodeId);
      }
    });

    return visible;
  }, [
    storeNodes,
    storeEdges,
    focusMode,
    focusedGraphId,
    activeConnectionType,
    expandedNodes,
    promotedChildren,
    isRootEntityNode,
    getNodeChildren,
    activeMode,
  ]);

  // D3 nodes ref for real-time updates during drag
  const d3NodesRef = useRef<D3Node[]>([]);
  const parentChildMapRef = useRef<Map<string, string[]>>(new Map());

  // Build hierarchical layout - positions children to the right of parents
  const buildHierarchicalLayout = useCallback(
    (flowNodes: Node<GraphNodeData>[], flowEdges: Edge[], centerX: number, centerY: number) => {
      if (flowNodes.length === 0) return;

      // Build parent-child relationships from edges
      const parentChildMap = new Map<string, string[]>();
      const childParentMap = new Map<string, string>();

      // Identify root nodes (those without parents in visible set)
      const visibleIds = new Set(flowNodes.map((n) => n.id));
      const childIds = new Set<string>();

      flowEdges.forEach((edge) => {
        if (visibleIds.has(edge.source) && visibleIds.has(edge.target)) {
          // Determine parent-child direction based on node types
          const sourceNode = flowNodes.find((n) => n.id === edge.source);
          const targetNode = flowNodes.find((n) => n.id === edge.target);

          if (sourceNode && targetNode) {
            const sourceType = (sourceNode.data as GraphNodeData).type;
            const targetType = (targetNode.data as GraphNodeData).type;

            // Define hierarchy based on mode
            // Activity: user/sa/resource(0) -> role/anomaly(1) -> resource(2)
            // Correlations/Blast: user/sa(0) -> secret(1) -> token(2) -> resource(3)
            const typeOrder: Record<string, number> =
              activeMode === "correlations" || activeMode === "blast-radius"
                ? { user: 0, service_account: 0, secret: 1, token: 2, resource: 3 }
                : { user: 0, service_account: 0, resource: 0, role: 1, anomaly: 1 };

            const sourceOrder = typeOrder[sourceType] ?? 2;
            const targetOrder = typeOrder[targetType] ?? 2;

            let parentId: string, childId: string;
            if (sourceOrder <= targetOrder) {
              parentId = edge.source;
              childId = edge.target;
            } else {
              parentId = edge.target;
              childId = edge.source;
            }

            if (!parentChildMap.has(parentId)) {
              parentChildMap.set(parentId, []);
            }
            if (!childParentMap.has(childId)) {
              parentChildMap.get(parentId)!.push(childId);
              childParentMap.set(childId, parentId);
              childIds.add(childId);
            }
          }
        }
      });

      parentChildMapRef.current = parentChildMap;

      // Identify root nodes
      const rootNodes = flowNodes.filter((n) => !childIds.has(n.id));

      // Position nodes hierarchically
      const positions = new Map<string, { x: number; y: number }>();
      const HORIZONTAL_SPACING = 700; // Space between hierarchy levels (longer edges)
      const VERTICAL_SPACING_CARD = 260; // Vertical space for card nodes
      const VERTICAL_SPACING_CIRCULAR = 180; // Vertical space for circular nodes

      // Position root nodes vertically centered
      const totalRootHeight = rootNodes.reduce((acc, node) => {
        const isCard = node.type === "cardNode";
        return acc + (isCard ? VERTICAL_SPACING_CARD : VERTICAL_SPACING_CIRCULAR);
      }, 0);

      let currentY = centerY - totalRootHeight / 2;
      rootNodes.forEach((node) => {
        const isCard = node.type === "cardNode";
        const spacing = isCard ? VERTICAL_SPACING_CARD : VERTICAL_SPACING_CIRCULAR;

        // Use cached position for root if dragged, otherwise layout position
        const cached = nodePositionsRef.current.get(node.id);
        if (cached && !childIds.has(node.id)) {
          positions.set(node.id, cached);
        } else {
          positions.set(node.id, { x: centerX - 200, y: currentY });
        }
        currentY += spacing;
      });

      // Position children recursively to the right of their parents
      const positionChildren = (parentId: string, level: number) => {
        const children = parentChildMap.get(parentId) || [];
        if (children.length === 0) return;

        const parentPos = positions.get(parentId);
        if (!parentPos) return;

        const totalChildHeight = children.reduce((acc, childId) => {
          const childNode = flowNodes.find((n) => n.id === childId);
          const isCard = childNode?.type === "cardNode";
          return acc + (isCard ? VERTICAL_SPACING_CARD : VERTICAL_SPACING_CIRCULAR);
        }, 0);

        let childY = parentPos.y - totalChildHeight / 2 + totalChildHeight / children.length / 2;

        children.forEach((childId) => {
          const childNode = flowNodes.find((n) => n.id === childId);
          const isCard = childNode?.type === "cardNode";
          const spacing = isCard ? VERTICAL_SPACING_CARD : VERTICAL_SPACING_CIRCULAR;

          positions.set(childId, {
            x: parentPos.x + HORIZONTAL_SPACING,
            y: childY,
          });
          childY += spacing;

          // Recursively position grandchildren
          positionChildren(childId, level + 1);
        });
      };

      // Position all children starting from roots
      rootNodes.forEach((node) => {
        positionChildren(node.id, 1);
      });

      // Apply positions to nodes
      setNodes((nds) =>
        nds.map((node) => {
          const pos = positions.get(node.id);
          if (pos) {
            nodePositionsRef.current.set(node.id, pos);
            return { ...node, position: pos };
          }
          return node;
        }),
      );
    },
    [setNodes, activeMode],
  );

  // Force layout for collision avoidance after hierarchical positioning
  const runForceLayout = useCallback(
    (
      flowNodes: Node<GraphNodeData>[],
      flowEdges: Edge[],
      centerX: number,
      centerY: number,
      isInitialLoad: boolean = false,
    ) => {
      if (simulationRef.current) {
        simulationRef.current.stop();
      }
      if (layoutTimeoutRef.current) {
        clearTimeout(layoutTimeoutRef.current);
      }

      // Skip hierarchical layout on initial load - let force simulation create the explosion effect
      if (!isInitialLoad) {
        buildHierarchicalLayout(flowNodes, flowEdges, centerX, centerY);
      }

      // Create D3 nodes from current positions
      // Build a map from node id to its graph node type for link distance logic
      const nodeDataMap = new Map<string, string>();
      flowNodes.forEach((node) => {
        const data = node.data as GraphNodeData;
        if (data?.type) nodeDataMap.set(node.id, data.type);
      });

      const d3Nodes: D3Node[] = flowNodes.map((node) => {
        const existingPos = nodePositionsRef.current.get(node.id);
        const offsetX = node.type === "cardNode" ? 120 : 45;
        const offsetY = node.type === "cardNode" ? 80 : 45;
        return {
          id: node.id,
          x: existingPos ? existingPos.x + offsetX : node.position.x + offsetX,
          y: existingPos ? existingPos.y + offsetY : node.position.y + offsetY,
          isCard: node.type === "cardNode",
          nodeType: nodeDataMap.get(node.id),
          fx: null,
          fy: null,
        };
      });

      d3NodesRef.current = d3Nodes;

      const d3Links: D3Link[] = flowEdges.map((edge) => ({
        source: edge.source,
        target: edge.target,
      }));

      // Check if this is an expanded activity graph (not initial load, activity mode)
      const isExpandedActivity = !isInitialLoad && activeMode === "activity";

      // Custom link distance for expanded activity graphs
      const baseLinkDistance = isInitialLoad ? 800 : 700;
      const linkDistanceFn = isExpandedActivity
        ? (link: D3Link) => {
          const sourceNode =
            typeof link.source === "object" ? link.source : d3Nodes.find((n) => n.id === link.source);
          const targetNode =
            typeof link.target === "object" ? link.target : d3Nodes.find((n) => n.id === link.target);
          const srcType = sourceNode?.nodeType;
          const tgtType = targetNode?.nodeType;

          // User/SA/IAMRole → Role: 5x longer
          const entityTypes = ["user", "service_account", "iam_role"];
          if (
            (entityTypes.includes(srcType || "") && tgtType === "role") ||
            (entityTypes.includes(tgtType || "") && srcType === "role")
          ) {
            return baseLinkDistance * 3;
          }

          // Role → Resource: 1/5 shorter
          if ((srcType === "role" && tgtType === "resource") || (tgtType === "role" && srcType === "resource")) {
            return baseLinkDistance / 3;
          }

          return baseLinkDistance;
        }
        : baseLinkDistance;

      // Stronger forces for initial load animation, gentler for subsequent updates
      // Increased repulsion for expanded activity graphs
      const chargeStrength = isInitialLoad ? -25000 : isExpandedActivity ? -50000 : -12000;

      const simulation = forceSimulation<D3Node>(d3Nodes)
        .force(
          "link",
          forceLink<D3Node, D3Link>(d3Links)
            .id((d) => d.id)
            .distance(linkDistanceFn as any)
            .strength(isInitialLoad ? 0.25 : 0.2),
        )
        .force("charge", forceManyBody<D3Node>().strength(chargeStrength))
        .force(
          "collide",
          forceCollide<D3Node>()
            .radius((d) => (d.isCard ? (isInitialLoad ? 650 : 500) : isInitialLoad ? 320 : 220))
            .strength(1)
            .iterations(4),
        )
        .force("centerX", forceX<D3Node>(centerX).strength(isInitialLoad ? 0.04 : 0.008))
        .force("centerY", forceY<D3Node>(centerY).strength(isInitialLoad ? 0.04 : 0.008))
        .alphaDecay(isInitialLoad ? 0.004 : 0.02)
        .velocityDecay(isInitialLoad ? 0.7 : 0.4)
        .alphaMin(0.01)
        .alpha(isInitialLoad ? 0.35 : 0.3);

      simulationRef.current = simulation;

      simulation.on("tick", () => {
        const nodeMap = new Map(d3Nodes.map((d) => [d.id, d]));

        setNodes((nds) =>
          nds.map((node) => {
            const d3Node = nodeMap.get(node.id);
            if (d3Node && d3Node.x !== undefined && d3Node.y !== undefined) {
              const offsetX = node.type === "cardNode" ? 120 : 45;
              const offsetY = node.type === "cardNode" ? 80 : 45;
              const newX = d3Node.x - offsetX;
              const newY = d3Node.y - offsetY;
              nodePositionsRef.current.set(node.id, { x: newX, y: newY });
              return {
                ...node,
                position: { x: newX, y: newY },
              };
            }
            return node;
          }),
        );
      });

      simulation.on("end", () => {
        // Layout complete - no auto zoom
      });
    },
    [setNodes, fitView, buildHierarchicalLayout, activeMode],
  );

  // Reheat simulation when node is released after drag (only when not beautified)
  const reheatSimulation = useCallback(() => {
    if (isBeautified) return; // Don't reheat in beautified mode - keep layout stable
    if (simulationRef.current) {
      // Release all fixed positions
      d3NodesRef.current.forEach((d3Node) => {
        d3Node.fx = null;
        d3Node.fy = null;
      });
      // Reheat the simulation
      simulationRef.current.alpha(0.5).restart();
    }
  }, [isBeautified]);

  // Handle node drag - fix position during drag
  const handleNodeDrag = useCallback(
    (nodeId: string, x: number, y: number) => {
      // In beautified mode, just update the position cache directly
      if (isBeautified) {
        nodePositionsRef.current.set(nodeId, { x, y });
        return;
      }
      const d3Node = d3NodesRef.current.find((n) => n.id === nodeId);
      if (d3Node) {
        const offsetX = d3Node.isCard ? 120 : 45;
        const offsetY = d3Node.isCard ? 80 : 45;
        d3Node.fx = x + offsetX;
        d3Node.fy = y + offsetY;
        d3Node.x = x + offsetX;
        d3Node.y = y + offsetY;
        // Keep simulation warm during drag
        if (simulationRef.current) {
          simulationRef.current.alpha(0.1).restart();
        }
      }
    },
    [isBeautified],
  );

  // Custom onNodesChange to handle drag events
  const handleNodesChange = useCallback(
    (changes: any[]) => {
      onNodesChange(changes);

      changes.forEach((change) => {
        if (change.type === "position" && change.dragging && change.position) {
          handleNodeDrag(change.id, change.position.x, change.position.y);
          // Dispatch event to collapse UI elements
          window.dispatchEvent(new CustomEvent("graph-interaction", { detail: { type: "drag-start" } }));
        } else if (change.type === "position" && !change.dragging) {
          // Drag ended - reheat simulation
          reheatSimulation();
        }
      });
    },
    [onNodesChange, handleNodeDrag, reheatSimulation],
  );

  // Handle expand button click (opens expansion panel)
  const handleExpandClick = useCallback(
    (nodeId: string) => {
      const storeNode = storeNodes.find((n) => n.id === nodeId);
      if (!storeNode) return;

      const graphId = storeNode.props?.graphId;
      const children = getNodeChildren(nodeId, storeNode.type);

      if (!focusMode && graphId) {
        const isRootNode =
          storeNode.type === "user" || storeNode.type === "service_account" || storeNode.type === "resource";
        const isNewFocusedGraph = focusedGraphId !== graphId;

        if (isRootNode && isNewFocusedGraph) {
          setFocusedGraph(graphId);
          if (children.length > 0) {
            setTimeout(() => openExpansionPanel(nodeId, children), 500);
          }
        } else if (children.length > 0) {
          openExpansionPanel(nodeId, children);
        }
      } else if (children.length > 0) {
        openExpansionPanel(nodeId, children);
      }
    },
    [storeNodes, getNodeChildren, focusMode, focusedGraphId, setFocusedGraph, openExpansionPanel],
  );

  // Handle selecting a child from connection panel
  const handleSelectChild = useCallback(
    (childId: string) => {
      if (activeExpansionPanel) {
        promoteChild(activeExpansionPanel.parentId, childId);
      }
    },
    [activeExpansionPanel, promoteChild],
  );

  // Build flow nodes data (separated from layout to avoid unnecessary re-layouts)
  const buildFlowNodes = useCallback(
    (visibleIds: Set<string>, centerX: number, centerY: number, isInitialLoad: boolean) => {
      const flowNodes: Node<GraphNodeData>[] = [];
      const visibleArray = Array.from(visibleIds);

      visibleArray.forEach((nodeId, idx) => {
        const storeNode = storeNodes.find((n) => n.id === nodeId);
        if (!storeNode) return;

        // Use cached position if available
        const cachedPos = nodePositionsRef.current.get(nodeId);
        let x: number, y: number;

        if (cachedPos) {
          x = cachedPos.x;
          y = cachedPos.y;
        } else if (isInitialLoad) {
          // Distribute nodes around the edges (left, right, top, bottom) for smooth inward glide
          const direction = idx % 4; // 0=left, 1=right, 2=top, 3=bottom
          const spreadOffset = (Math.random() - 0.5) * 800; // Vertical/horizontal spread

          switch (direction) {
            case 0: // From left
              x = centerX - 1800 + Math.random() * 300;
              y = centerY + spreadOffset;
              break;
            case 1: // From right
              x = centerX + 1800 - Math.random() * 300;
              y = centerY + spreadOffset;
              break;
            case 2: // From top
              x = centerX + spreadOffset;
              y = centerY - 1200 + Math.random() * 300;
              break;
            case 3: // From bottom
              x = centerX + spreadOffset;
              y = centerY + 1200 - Math.random() * 300;
              break;
            default:
              x = centerX + (Math.random() - 0.5) * 100;
              y = centerY + (Math.random() - 0.5) * 100;
          }
        } else {
          // Grid layout for subsequent loads
          const cols = Math.ceil(Math.sqrt(visibleArray.length || 1));
          const spacing = 200;
          const col = idx % cols;
          const row = Math.floor(idx / cols);
          x = centerX - (cols * spacing) / 2 + col * spacing;
          y = centerY - (Math.ceil(visibleArray.length / cols) * spacing) / 2 + row * spacing;
        }

        // SaaS resource nodes (from correlations/blast radius) render as cards, not circular
        const isSaasResource =
          !!(storeNode.props?.isCorrelationMode || storeNode.props?.isBlastRadiusMode) && storeNode.type === "resource";
        const isCircular =
          !isSaasResource &&
          (storeNode.type === "anomaly" ||
            storeNode.type === "role" ||
            storeNode.type === "secret" ||
            storeNode.type === "token");
        const children = getNodeChildren(storeNode.id, storeNode.type);
        const isExpanded = expandedNodes.has(storeNode.id);
        const collapsedList = collapsedChildren.get(storeNode.id);
        const collapsedCount = collapsedList?.length || (isExpanded && children.length > 3 ? children.length - 3 : 0);

        // Calculate counts
        const nodeEdges = storeEdges.filter((e) => e.source === storeNode.id || e.target === storeNode.id);
        const connectedNodeIds = nodeEdges.map((e) => (e.source === storeNode.id ? e.target : e.source));
        const connectedNodes = storeNodes.filter((n) => connectedNodeIds.includes(n.id));

        // Use alertCount from node properties (set from API) if available, otherwise count connected anomalies
        const alertCount = storeNode.props?.alertCount ?? connectedNodes.filter((n) => n.type === "anomaly").length;
        const roleCount = connectedNodes.filter((n) => n.type === "role").length;
        const userCount =
          storeNode.props?.userCount ??
          connectedNodes.filter((n) => n.type === "user" || n.type === "service_account").length;

        let resourceCount = 0;
        if (storeNode.type === "user" || storeNode.type === "service_account") {
          const roleIds = connectedNodes.filter((n) => n.type === "role").map((n) => n.id);
          const resourceIds = new Set<string>();
          roleIds.forEach((roleId) => {
            const roleEdges = storeEdges.filter((e) => e.source === roleId || e.target === roleId);
            roleEdges.forEach((e) => {
              const targetId = e.source === roleId ? e.target : e.source;
              const targetNode = storeNodes.find((n) => n.id === targetId);
              if (targetNode?.type === "resource") resourceIds.add(targetId);
            });
          });
          resourceCount = resourceIds.size;
        } else {
          resourceCount = connectedNodes.filter((n) => n.type === "resource").length;
        }

        // Build connection children for this node
        const connectionChildren: ConnectionChild[] = children
          .map((childId) => {
            const childNode = storeNodes.find((n) => n.id === childId);
            if (!childNode) return null;

            const childEdges = storeEdges.filter((e) => e.source === childId || e.target === childId);
            const hasAnomaly = childEdges.some((e) => {
              const otherId = e.source === childId ? e.target : e.source;
              const otherNode = storeNodes.find((n) => n.id === otherId);
              return otherNode?.type === "anomaly";
            });

            return {
              id: childNode.id,
              label: childNode.label,
              type: childNode.type,
              resourceType: childNode.props?.resourceType,
              hasAnomaly,
            } as ConnectionChild;
          })
          .filter((c): c is ConnectionChild => c !== null);

        const isPanelOpen = activeExpansionPanel?.parentId === storeNode.id;

        flowNodes.push({
          id: storeNode.id,
          type: isCircular ? "circularNode" : "cardNode",
          position: { x, y },
          zIndex: isPanelOpen ? 9999 : 1,
          data: {
            label: storeNode.label,
            type: storeNode.type,
            email: storeNode.props?.email,
            provider: storeNode.provider,
            cloudProvider: storeNode.props?.cloudProvider,
            resourceType: storeNode.props?.resourceType,
            activity: isSaasResource ? undefined : storeNode.props?.activity,
            lastActivityMessage: isSaasResource ? undefined : storeNode.props?.lastActivityMessage,
            // SaaS resource metadata
            isSaasResource,
            saasType: storeNode.props?.saasType,
            saasResourceType: storeNode.props?.resourceType,
            riskScore: storeNode.risk_score,
            alertCount,
            roleCount,
            resourceCount,
            userCount,
            childCount: children.length,
            collapsedCount,
            isExpanded,
            hasUnexpandedChildren: connectionChildren.length > (promotedChildren.get(storeNode.id)?.size || 0),
            graphId: storeNode.props?.graphId,
            isHighlighted: highlightedNodes.has(storeNode.id),
            nodeId: storeNode.id,
            isConnectionPanelOpen: isPanelOpen,
            connectionChildren,
            expandedChildIds: promotedChildren.get(storeNode.id) || new Set<string>(),
            onExpandClick: handleExpandClick,
            onSelectChild: handleSelectChild,
            onClosePanel: closeExpansionPanel,
            hideExpandButton: false, // Always show expand buttons in all modes
          },
        });
      });

      return flowNodes;
    },
    [
      storeNodes,
      storeEdges,
      expandedNodes,
      collapsedChildren,
      highlightedNodes,
      getNodeChildren,
      activeExpansionPanel,
      handleExpandClick,
      handleSelectChild,
      closeExpansionPanel,
      promotedChildren,
    ],
  );

  // Convert store data to React Flow format
  useEffect(() => {
    const containerWidth = containerRef.current?.offsetWidth || 800;
    const containerHeight = containerRef.current?.offsetHeight || 600;
    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;

    // Check if this is initial load (no previous visible IDs)
    const visibleArray = Array.from(visibleNodeIds);
    const currentVisibleIds = visibleArray.sort().join(",");
    const isInitialLoad = lastVisibleIdsRef.current === "";

    const flowNodes = buildFlowNodes(visibleNodeIds, centerX, centerY, isInitialLoad);

    // Build node level map for edge styling
    const nodeLevelMap = new Map<string, number>();
    const typeToLevel: Record<string, number> = {
      user: 0,
      service_account: 0,
      resource: 0,
      anomaly: 1,
      role: 1,
    };
    visibleArray.forEach((nodeId) => {
      const sn = storeNodes.find((n) => n.id === nodeId);
      if (sn) nodeLevelMap.set(nodeId, typeToLevel[sn.type] ?? 2);
    });

    // Create edges with curve offsets for parallel edges
    const flowEdges: Edge[] = [];

    // Group edges by node pairs to handle parallel edges
    const edgePairs = new Map<string, typeof storeEdges>();
    storeEdges.forEach((edge) => {
      if (visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)) {
        const pairKey = [edge.source, edge.target].sort().join("|");
        if (!edgePairs.has(pairKey)) {
          edgePairs.set(pairKey, []);
        }
        edgePairs.get(pairKey)!.push(edge);
      }
    });

    // Create curved edges with index data for parallel edge handling
    edgePairs.forEach((edgesInPair) => {
      edgesInPair.forEach((edge, index) => {
        flowEdges.push({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          type: "curved",
          data: {
            edgeIndex: index,
            totalEdges: edgesInPair.length,
            sourceLevel: nodeLevelMap.get(edge.source) ?? 0,
            targetLevel: nodeLevelMap.get(edge.target) ?? 0,
          },
          style: {
            stroke: theme === "dark" ? "rgba(140, 140, 140, 0.8)" : "rgba(100, 100, 100, 0.7)",
            strokeWidth: 1.5,
          },
        });
      });
    });

    setNodes(flowNodes);
    setEdges(flowEdges);

    // Only run force simulation when visible node IDs actually change (not on panel open/close)
    if (currentVisibleIds !== lastVisibleIdsRef.current && flowNodes.length > 0) {
      lastVisibleIdsRef.current = currentVisibleIds;
      // Clear position cache for removed nodes
      const currentIds = new Set(visibleArray);
      nodePositionsRef.current.forEach((_, key) => {
        if (!currentIds.has(key)) nodePositionsRef.current.delete(key);
      });
      runForceLayout(flowNodes, flowEdges, centerX, centerY, isInitialLoad);
    }
  }, [visibleNodeIds, storeNodes, storeEdges, theme, buildFlowNodes, setNodes, setEdges, runForceLayout]);

  // Update node data without re-running layout (for panel open/close, highlighting, etc.)
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => {
        const storeNode = storeNodes.find((n) => n.id === node.id);
        if (!storeNode) return node;

        const isPanelOpen = activeExpansionPanel?.parentId === node.id;
        const children = getNodeChildren(node.id, storeNode.type);

        const connectionChildren: ConnectionChild[] = children
          .map((childId) => {
            const childNode = storeNodes.find((n) => n.id === childId);
            if (!childNode) return null;
            const childEdges = storeEdges.filter((e) => e.source === childId || e.target === childId);
            const hasAnomaly = childEdges.some((e) => {
              const otherId = e.source === childId ? e.target : e.source;
              const otherNode = storeNodes.find((n) => n.id === otherId);
              return otherNode?.type === "anomaly";
            });
            return {
              id: childNode.id,
              label: childNode.label,
              type: childNode.type,
              resourceType: childNode.props?.resourceType,
              hasAnomaly,
            } as ConnectionChild;
          })
          .filter((c): c is ConnectionChild => c !== null);

        const promotedSet = promotedChildren.get(node.id) || new Set<string>();

        return {
          ...node,
          zIndex: isPanelOpen ? 9999 : 1,
          data: {
            ...node.data,
            childCount: children.length,
            // Show stacked effect only if there are unexplored children
            hasUnexpandedChildren: connectionChildren.length > promotedSet.size,
            isConnectionPanelOpen: isPanelOpen,
            connectionChildren,
            expandedChildIds: promotedSet,
            isHighlighted: highlightedNodes.has(node.id),
            onExpandClick: handleExpandClick,
            onSelectChild: handleSelectChild,
            onClosePanel: closeExpansionPanel,
          },
        };
      }),
    );
  }, [
    activeExpansionPanel,
    highlightedNodes,
    storeNodes,
    storeEdges,
    getNodeChildren,
    handleExpandClick,
    handleSelectChild,
    closeExpansionPanel,
    setNodes,
    promotedChildren,
  ]);

  // Handle node click with zoom animation
  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      selectNode(node.id);
      // Trigger zoom to clicked node
      setTriggerZoomToNode(node.id);
      // Dispatch event to collapse UI elements
      window.dispatchEvent(new CustomEvent("graph-interaction", { detail: { type: "node-click" } }));
    },
    [selectNode, setTriggerZoomToNode],
  );

  // Handle context menu - open immediately at node position (no delay)
  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.preventDefault();
      const storeNode = storeNodes.find((n) => n.id === node.id);
      if (storeNode) {
        selectNode(node.id);
        // Trigger zoom to right-clicked node
        setTriggerZoomToNode(node.id);
        // Dispatch event to collapse UI elements
        window.dispatchEvent(new CustomEvent("graph-interaction", { detail: { type: "node-context" } }));
        // Open context menu immediately
        setContextMenu({
          isOpen: true,
          node: { id: node.id, label: storeNode.label, type: storeNode.type },
        });
      }
    },
    [storeNodes, selectNode, setTriggerZoomToNode],
  );

  // Expansion panel data
  const expansionPanelNode = useMemo(() => {
    if (!activeExpansionPanel) return null;
    return nodes.find((n) => n.id === activeExpansionPanel.parentId);
  }, [activeExpansionPanel, nodes]);

  const expansionPanelChildren = useMemo(() => {
    if (!activeExpansionPanel) return [];
    return activeExpansionPanel.childIds
      .map((id) => storeNodes.find((n) => n.id === id))
      .filter((n): n is GraphNode => n !== null);
  }, [activeExpansionPanel, storeNodes]);

  const handleShowFullGraph = useCallback(
    (nodeId: string) => {
      const clickedNode = storeNodes.find((n) => n.id === nodeId);
      if (!clickedNode) return;

      const isSpecializedMode = activeMode === "correlations" || activeMode === "blast-radius";
      const graphId = clickedNode.props?.graphId;

      closeExpansionPanel();

      const maxDepth = isSpecializedMode ? Number.POSITIVE_INFINITY : SHOW_FULL_GRAPH_DEPTH[clickedNode.type] || 0;
      if (maxDepth === 0) return;

      const expandWithDepthLimit = () => {
        const expansionsQueue: Array<{ parentId: string; childId: string }> = [];
        const visited = new Set<string>();

        const collectExpansions = (id: string, currentDepth: number) => {
          if (visited.has(id)) return;
          if (currentDepth >= maxDepth) return;
          visited.add(id);

          const node = storeNodes.find((n) => n.id === id);
          if (!node) return;
          if (!isSpecializedMode && graphId && node.props?.graphId !== graphId) return;
          if (node.type === "anomaly") return;

          const allowedChildTypes = INFINITE_HIERARCHY_MAP[node.type] || [];

          storeEdges.forEach((edge) => {
            let childId: string | null = null;
            if (edge.source === id) childId = edge.target;
            else if (edge.target === id) childId = edge.source;

            if (childId && !visited.has(childId)) {
              const childNode = storeNodes.find((n) => n.id === childId);
              if (
                childNode &&
                childNode.type !== "permission" &&
                childNode.type !== "anomaly" &&
                allowedChildTypes.includes(childNode.type) &&
                (isSpecializedMode || !graphId || childNode.props?.graphId === graphId)
              ) {
                expansionsQueue.push({ parentId: id, childId });
                collectExpansions(childId, currentDepth + 1);
              }
            }
          });
        };

        collectExpansions(nodeId, 0);

        expansionsQueue.forEach(({ parentId, childId }) => {
          promoteChild(parentId, childId);
        });
      };

      const isAlreadyFocused = !isSpecializedMode && graphId ? focusedGraphId === graphId : true;

      if (!isSpecializedMode && graphId && !isAlreadyFocused) {
        setFocusedGraph(graphId);
        setTimeout(expandWithDepthLimit, 800);
      } else {
        expandWithDepthLimit();
      }
    },
    [storeNodes, storeEdges, activeMode, closeExpansionPanel, promoteChild, focusedGraphId, setFocusedGraph],
  );

  // Custom BFS beautify layout - triggered by beautify button
  useEffect(() => {
    if (triggerBeautify === 0) return;

    const currentNodes = getNodes();
    if (currentNodes.length === 0) return;

    // Stop any running force simulation
    if (simulationRef.current) {
      simulationRef.current.stop();
    }

    const currentEdges = edges;
    const nodeIds = new Set(currentNodes.map((n) => n.id));

    // --- Step 1: Assign levels & find root ancestors ---
    const nodeDataMap = new Map(currentNodes.map((n) => [n.id, n.data as GraphNodeData]));

    // Build adjacency from edges
    const adjacency = new Map<string, Set<string>>();
    currentNodes.forEach((n) => adjacency.set(n.id, new Set()));
    currentEdges.forEach((edge) => {
      if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
        adjacency.get(edge.source)!.add(edge.target);
        adjacency.get(edge.target)!.add(edge.source);
      }
    });

    // Assign levels based on node type
    const typeLevelMap: Record<string, number> = {
      user: 0,
      service_account: 0,
      resource: 2,
      role: 1,
      anomaly: -1,
    };

    const levelMap = new Map<string, number>();
    currentNodes.forEach((n) => {
      const d = nodeDataMap.get(n.id);
      const nodeType = d?.type || "";
      levelMap.set(n.id, typeLevelMap[nodeType] ?? 2);
    });

    // Identify root nodes (level 0: user/service_account)
    const rootNodes = currentNodes.filter((n) => {
      const d = nodeDataMap.get(n.id);
      return d?.type === "user" || d?.type === "service_account";
    });

    // BFS from each root to assign ownership (which root each node belongs to)
    const nodeOwner = new Map<string, string>(); // nodeId -> rootId
    rootNodes.forEach((root) => {
      const queue = [root.id];
      const visited = new Set<string>();
      visited.add(root.id);
      nodeOwner.set(root.id, root.id);

      while (queue.length > 0) {
        const current = queue.shift()!;
        const neighbors = adjacency.get(current) || new Set();
        neighbors.forEach((neighborId) => {
          if (!visited.has(neighborId) && nodeIds.has(neighborId)) {
            visited.add(neighborId);
            if (!nodeOwner.has(neighborId)) {
              nodeOwner.set(neighborId, root.id);
            }
            queue.push(neighborId);
          }
        });
      }
    });

    // Assign orphan nodes (no root connection) to a virtual root
    currentNodes.forEach((n) => {
      if (!nodeOwner.has(n.id)) {
        nodeOwner.set(n.id, "__orphan__");
      }
    });

    // --- Step 2: Group nodes by (level, owner) ---
    const levelGroups = new Map<number, typeof currentNodes>();
    currentNodes.forEach((n) => {
      const level = levelMap.get(n.id) ?? 0;
      if (!levelGroups.has(level)) levelGroups.set(level, []);
      levelGroups.get(level)!.push(n);
    });

    const maxLevel = Math.max(...Array.from(levelGroups.keys()), 0);
    const minLevel = Math.min(...Array.from(levelGroups.keys()), 0);

    // --- Step 3: Compute positions per-root within each level ---
    const containerWidth = containerRef.current?.offsetWidth || 1200;
    const containerHeight = containerRef.current?.offsetHeight || 800;

    const LEVEL_GAP_X = 1200;
    const ZIG_ZAG_X_CARD = 900;
    const ZIG_ZAG_X_CIRCLE = 200;
    const NODE_GAP_Y_CARD = 250;
    const NODE_GAP_Y_CIRCLE = 280;
    const ROOT_GROUP_GAP = 400; // Extra vertical gap between different root groups

    const totalWidth = (maxLevel - minLevel) * LEVEL_GAP_X;
    const startX = (containerWidth - totalWidth) / 2 - minLevel * LEVEL_GAP_X;

    const targetPositions = new Map<string, { x: number; y: number }>();

    // Get ordered root IDs (use the order they appear in rootNodes + orphan)
    const rootOrder = rootNodes.map((r) => r.id);
    if (currentNodes.some((n) => nodeOwner.get(n.id) === "__orphan__")) {
      rootOrder.push("__orphan__");
    }

    levelGroups.forEach((nodesInLevel, level) => {
      const levelBaseX = startX + level * LEVEL_GAP_X;

      // Group nodes in this level by their owner root
      const byOwner = new Map<string, typeof currentNodes>();
      nodesInLevel.forEach((n) => {
        const owner = nodeOwner.get(n.id) || "__orphan__";
        if (!byOwner.has(owner)) byOwner.set(owner, []);
        byOwner.get(owner)!.push(n);
      });

      // Calculate total height for all groups in this level
      let totalLevelHeight = 0;
      const groupHeights: { owner: string; nodes: typeof currentNodes; height: number; gapY: number; zigX: number }[] =
        [];

      rootOrder.forEach((owner) => {
        const groupNodes = byOwner.get(owner);
        if (!groupNodes || groupNodes.length === 0) return;

        const hasCards = groupNodes.some((n) => n.type === "cardNode");
        const gapY = hasCards ? NODE_GAP_Y_CARD : NODE_GAP_Y_CIRCLE;
        const zigX = hasCards ? ZIG_ZAG_X_CARD : ZIG_ZAG_X_CIRCLE;
        const groupHeight = (groupNodes.length - 1) * gapY;

        groupHeights.push({ owner, nodes: groupNodes, height: groupHeight, gapY, zigX });
        totalLevelHeight += groupHeight;
      });

      // Add gaps between groups
      totalLevelHeight += Math.max(0, groupHeights.length - 1) * ROOT_GROUP_GAP;

      let currentY = (containerHeight - totalLevelHeight) / 2;

      groupHeights.forEach(({ nodes: groupNodes, height, gapY, zigX }) => {
        groupNodes.forEach((node, idx) => {
          const xOffset = idx % 2 === 0 ? -zigX / 2 : zigX / 2;
          const nodeW = node.type === "cardNode" ? 190 : 80;
          const nodeH = node.type === "cardNode" ? 130 : 80;

          targetPositions.set(node.id, {
            x: levelBaseX + xOffset - nodeW,
            y: currentY + idx * gapY - nodeH,
          });
        });
        currentY += height + ROOT_GROUP_GAP;
      });
    });

    // --- Step 4: Animate ---
    const duration = 900;
    const startTime = Date.now();
    const startPositions = new Map(currentNodes.map((n) => [n.id, { ...n.position }]));

    setBeautified(true);

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);

      setNodes((nds) =>
        nds.map((node) => {
          const target = targetPositions.get(node.id);
          const startPos = startPositions.get(node.id);
          if (!target || !startPos) return node;

          const newX = startPos.x + (target.x - startPos.x) * eased;
          const newY = startPos.y + (target.y - startPos.y) * eased;

          nodePositionsRef.current.set(node.id, { x: newX, y: newY });
          return { ...node, position: { x: newX, y: newY } };
        }),
      );

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Start a gentle force simulation from beautified positions for fluid drag behavior
        setTimeout(() => {
          setBeautified(false);
          const currentFlowNodes = getNodes() as Node<GraphNodeData>[];
          const currentFlowEdges = edges;

          // Create D3 nodes from beautified positions
          const d3Nodes: D3Node[] = currentFlowNodes.map((node) => {
            const pos = nodePositionsRef.current.get(node.id) || node.position;
            const offsetX = node.type === "cardNode" ? 120 : 45;
            const offsetY = node.type === "cardNode" ? 80 : 45;
            return {
              id: node.id,
              x: pos.x + offsetX,
              y: pos.y + offsetY,
              isCard: node.type === "cardNode",
              fx: null,
              fy: null,
            };
          });

          d3NodesRef.current = d3Nodes;

          const d3Links: D3Link[] = currentFlowEdges.map((edge) => ({
            source: edge.source,
            target: edge.target,
          }));

          // Very gentle simulation - just collision avoidance, weak centering, preserves layout
          const containerW = containerRef.current?.offsetWidth || 1200;
          const containerH = containerRef.current?.offsetHeight || 800;

          const simulation = forceSimulation<D3Node>(d3Nodes)
            .force(
              "link",
              forceLink<D3Node, D3Link>(d3Links)
                .id((d) => d.id)
                .distance(400)
                .strength(0.03), // Very weak link pull
            )
            .force("charge", forceManyBody<D3Node>().strength(-800)) // Mild repulsion
            .force(
              "collide",
              forceCollide<D3Node>()
                .radius((d) => (d.isCard ? 200 : 100))
                .strength(0.5),
            )
            .force("centerX", forceX<D3Node>(containerW / 2).strength(0.002))
            .force("centerY", forceY<D3Node>(containerH / 2).strength(0.002))
            .alphaDecay(0.05) // Decays quickly so it settles near beautified positions
            .velocityDecay(0.6) // High damping
            .alphaMin(0.01)
            .alpha(0.05); // Start with very low energy

          simulationRef.current = simulation;

          simulation.on("tick", () => {
            const nodeMap = new Map(d3Nodes.map((d) => [d.id, d]));
            setNodes((nds) =>
              nds.map((node) => {
                const d3Node = nodeMap.get(node.id);
                if (d3Node && d3Node.x !== undefined && d3Node.y !== undefined) {
                  const offX = node.type === "cardNode" ? 120 : 45;
                  const offY = node.type === "cardNode" ? 80 : 45;
                  const newX = d3Node.x - offX;
                  const newY = d3Node.y - offY;
                  nodePositionsRef.current.set(node.id, { x: newX, y: newY });
                  return { ...node, position: { x: newX, y: newY } };
                }
                return node;
              }),
            );
          });
        }, 600);
      }
    };

    requestAnimationFrame(animate);
  }, [triggerBeautify]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset beautified state when visible nodes change (new data, focus change, etc.)
  useEffect(() => {
    if (isBeautified) {
      setBeautified(false);
    }
  }, [visibleNodeIds]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={containerRef} className="relative w-full h-full">
      {/* Custom grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundColor: theme === "dark" ? "#1a1a1a" : "#fafafa",
          backgroundImage:
            theme === "dark"
              ? `
              radial-gradient(circle, rgba(80,80,80,0.4) 2px, transparent 2px),
              linear-gradient(rgba(48,48,48,0.4) 1px, transparent 1px),
              linear-gradient(90deg, rgba(48,48,48,0.4) 1px, transparent 1px)
            `
              : `
              radial-gradient(circle, rgba(168,168,168,0.4) 2px, transparent 2px),
              linear-gradient(rgba(216,216,216,0.4) 1px, transparent 1px),
              linear-gradient(90deg, rgba(216,216,216,0.4) 1px, transparent 1px)
            `,
          backgroundSize: "40px 40px, 40px 40px, 40px 40px",
          backgroundPosition: "20px 20px, 0 0, 0 0",
        }}
      />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onNodeContextMenu={onNodeContextMenu}
        onNodeDragStart={() => {
          window.dispatchEvent(new CustomEvent("graph-interaction", { detail: { type: "drag-start" } }));
        }}
        onMoveStart={() => {
          window.dispatchEvent(new CustomEvent("graph-interaction", { detail: { type: "drag-start" } }));
        }}
        onPaneClick={() => {
          closeExpansionPanel();
          window.dispatchEvent(new CustomEvent("graph-interaction", { detail: { type: "pane-click" } }));
        }}
        onMove={() => {
          setZoom(getZoom());
          // Force viewport update for context menu tracking
          setViewportTrigger((prev) => prev + 1);
        }}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionMode={ConnectionMode.Loose}
        defaultViewport={{ x: 0, y: 0, zoom: 0.4 }}
        minZoom={0.1}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        className="!bg-transparent"
      />

      <ZoomControls
        zoom={zoom}
        minZoom={0.1}
        maxZoom={2}
        onZoomChange={(newZoom) => {
          const viewport = getViewport();
          setViewport({ ...viewport, zoom: newZoom });
          setZoom(newZoom);
        }}
      />

      <NodeContextMenu
        isOpen={contextMenu.isOpen}
        nodeId={contextMenu.node?.id || null}
        nodeLabel={contextMenu.node?.label || ""}
        nodeType={contextMenu.node?.type || null}
        focusMode={focusMode}
        hasExpandedChildren={contextMenu.node?.id ? promotedChildren.has(contextMenu.node.id) : false}
        onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
        onCollapseStep={collapseStep}
        onShowFullGraph={handleShowFullGraph}
        onToggleFocusMode={toggleFocusMode}
        flowNodes={nodes}
        getViewport={getViewport}
        containerRef={containerRef}
        viewportTrigger={viewportTrigger}
      />
    </div>
  );
}

export function ReactFlowGraph() {
  return (
    <ReactFlowProvider>
      <ReactFlowGraphInner />
    </ReactFlowProvider>
  );
}
